import { useEffect, useState } from 'react';

import jobseekersService from 'features/jobseekers/jobseekersService';

import { Education as EducationType, WorkExperience as WorkExperienceType } from 'types/commons';

import { useTranslation } from 'react-i18next';
import useErrorCatch from './useErrorCatch';

interface useFetchEducationAndExperienceProps {
  jobseekerId?: string | null;
}

export const useFetchEducationAndExperience = ({
  jobseekerId
}: useFetchEducationAndExperienceProps) => {
  const { t } = useTranslation()
  const catchError = useErrorCatch();
  const [education, setEducation] = useState<EducationType | null>(null);
  const [workExperience, setWorkExperience] =
    useState<WorkExperienceType | null>(null);
  const [isLoadingEducation, setIsLoadingEducation] = useState<boolean>(false);
  const [isLoadingWorkExperience, setIsLoadingWorkExperience] =
    useState<boolean>(false);

  const fetchEducationData = async (jobseekerId: string): Promise<void> => {
    try {
      setIsLoadingEducation(true);
      const result = await jobseekersService.getJobseekerEducation(jobseekerId);

      if (result?.records?.length) {
        setEducation(result);
      }
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.fetchJobseekerEducation')
        }
      });
    } finally {
      setIsLoadingEducation(false);
    }
  };

  const fetchWorkExperienceData = async (
    jobseekerId: string
  ): Promise<void> => {
    try {
      setIsLoadingWorkExperience(true);
      const result =
        await jobseekersService.getJobseekerWorkExperience(jobseekerId);

      if (result?.records?.length) {
        setWorkExperience(result);
      }
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.fetchJobseekerExperience')
        }
      });
    } finally {
      setIsLoadingWorkExperience(false);
    }
  };

  useEffect(() => {
    setEducation(null);
    setWorkExperience(null);
    if (!jobseekerId) {
      return;
    }

    fetchEducationData(jobseekerId);
    fetchWorkExperienceData(jobseekerId);
  }, [jobseekerId]);

  return {
    education,
    workExperience,
    isLoadingEducation,
    isLoadingWorkExperience
  };
};
